import * as React from "react";
import { useNavigate } from "react-router";
import NewRiskPage from "../../pages/NewRiskPage";
import { OpenWindowConfig, useWindowManager } from "./WindowManagerContext";
import { PageChromeContext } from "./pageChrome";
import WindowConfirmDialog from "./WindowConfirmDialog";

export type RiskFormWindowProps = {
  windowId: string;
  /**
   * Vorausgewählter Risiko-Typ-Key (z.B. "kfz", "immobilie", "machine", …).
   */
  riskType?: string;
  contactId?: number | null;
  contactName?: string | null;
  attachToContractId?: number | null;
  /** Wenn gesetzt: Edit-Mode — NewRiskPage fetched das Risiko und prefilled die Step-Form. */
  editRiskId?: number | null;
};

const riskTypeLabel = (t?: string): string => {
  switch (t) {
    case "kfz":
      return "KFZ-Risiko";
    case "immobilie":
      return "Immobilie-Risiko";
    case "machine":
    case "maschinen":
      return "Maschinen-Risiko";
    case "transport":
      return "Transport-Risiko";
    case "cyber":
      return "Cyber-Risiko";
    case "environment":
    case "umwelt":
      return "Umwelt-Risiko";
    case "liquidity":
    case "liquiditaet":
      return "Liquiditäts-Risiko";
    default:
      return "Risiko";
  }
};

// Modul-lokaler Draft-Counter — Fallback wenn weder Kunden-Name noch
// Kontakt-ID bekannt sind, damit Taskbar-Einträge trotzdem unterscheidbar
// bleiben ("Entwurf #1", "#2", …).
let riskDraftCounter = 0;

/**
 * Öffnet ein Risk-Form-Window mit aussagekräftigem Titel.
 *
 * Titel-Schema: `Neues {Typ}-Risiko · {Kundenname|Kontakt #ID|Entwurf #N}`.
 * Sobald der User später im Formular Kennzeichen / Adresse tippt,
 * aktualisiert NewRiskPage den Titel via `setWindowTitle`.
 */
export const openRiskFormWindow = (
  openWindow: (config: OpenWindowConfig) => void,
  params: {
    riskType?: string;
    contactId?: number | null;
    contactName?: string | null;
    attachToContractId?: number | null;
    editRiskId?: number | null;
    initialSize?: { width: number; height: number };
    /**
     * Darstellung beim Öffnen. Default `"page"` (Vollseite); `"window"` öffnet
     * direkt als maximiertes Fenster mit Titelleiste.
     */
    mode?: "page" | "window";
    /**
     * Eigene, mount-stabile ID. Nötig für Route-Einstiege (NewRiskRoute), weil
     * die intern gebaute ID `Date.now()` enthält und unter StrictMode sonst
     * zwei Fenster entstünden.
     */
    windowId?: string;
  },
): string => {
  const {
    riskType,
    contactId,
    contactName,
    attachToContractId,
    editRiskId,
    initialSize,
    mode = "page",
    windowId: explicitWindowId,
  } = params;
  const typeLabel = riskTypeLabel(riskType);
  const trimmedName = contactName?.trim();
  const nameSuffix = trimmedName
    ? trimmedName
    : contactId
      ? `Kontakt #${contactId}`
      : `Entwurf #${++riskDraftCounter}`;
  const title = editRiskId
    ? `${typeLabel} bearbeiten · ${nameSuffix}`
    : `Neues ${typeLabel} · ${nameSuffix}`;
  // Stable ID im Edit-Mode → mehrfacher Klick fokussiert dasselbe Window
  // statt parallele Edit-Fenster für dasselbe Risiko zu öffnen.
  const windowId =
    explicitWindowId ??
    (editRiskId
      ? `risk-edit-${editRiskId}`
      : `risk-create-${riskType ?? "generic"}-c${contactId ?? "new"}-${Date.now()}`);
  openWindow({
    id: windowId,
    title,
    initialSize: initialSize ?? { width: 1140, height: 760 },
    // Standard: als VOLLSEITE öffnen (ohne Titelleiste, ohne Fensterliste).
    // „Als Fenster" fällt auf initialSize zurück.
    initialMode: mode === "page" ? "page" : "maximized",
    render: () => (
      <RiskFormWindow
        windowId={windowId}
        riskType={riskType}
        contactId={contactId ?? null}
        contactName={contactName ?? null}
        attachToContractId={attachToContractId ?? null}
        editRiskId={editRiskId ?? null}
      />
    ),
  });
  return windowId;
};

/** Baut den Titel-Präfix (Typ-Label + Kunden-Suffix) — wird auch für Live-Updates wiederverwendet. */
export const buildRiskTitleBase = (
  riskType: string | undefined,
  contactName: string | null | undefined,
  contactId: number | null | undefined,
  isEdit?: boolean,
): string => {
  const typeLabel = riskTypeLabel(riskType);
  const trimmedName = contactName?.trim();
  const suffix = trimmedName ? trimmedName : contactId ? `Kontakt #${contactId}` : `Entwurf`;
  return isEdit ? `${typeLabel} bearbeiten · ${suffix}` : `Neues ${typeLabel} · ${suffix}`;
};

/**
 * Rendert NewRiskPage in einer Window-Manager-Card. Bekommt die Props
 * (riskType, contactId, …) direkt — kein MemoryRouter, weil
 * React Router v6 keine verschachtelten Router erlaubt.
 *
 * Nach Save (onSuccess) wird das Window geschlossen und, falls ein
 * Kontaktkontext gegeben ist, dorthin navigiert.
 */
const RiskFormWindow: React.FC<RiskFormWindowProps> = ({
  windowId,
  riskType,
  contactId,
  contactName,
  attachToContractId,
  editRiskId,
}) => {
  const { windows, openWindow, closeWindow, setWindowTitle, setWindowDirty, minimizeWindow } =
    useWindowManager();
  const navigate = useNavigate();

  const self = windows.find((w) => w.id === windowId);
  const isPage = self?.state === "page";

  /**
   * Fenster-Zustand als Ref statt als Callback-Dependency. Grund: `isPage` und
   * `isDirty` ändern sich bei JEDEM Moduswechsel („Als Fenster", „Minimieren").
   * Stünden sie in den Deps von `handleReplace`/`handleClose`, bekäme das
   * `embedded`-Objekt dort eine neue Identität — und die useEffects der
   * Step-Formulare (`[answers, onAnswersChange]`) feuerten erneut, obwohl der
   * User nichts eingegeben hat. Ergebnis wäre ein Dirty-Flag, das allein durch
   * das Umschalten anspringt (Regressionstest: riskFormWindowEmbedded.test.tsx).
   */
  const selfRef = React.useRef(self);
  selfRef.current = self;

  const handleSuccess = React.useCallback(() => {
    // Erst das Dirty-Flag löschen, sonst fragt der Close-Guard nach dem
    // erfolgreichen Speichern noch einmal nach.
    setWindowDirty(windowId, false);
    closeWindow(windowId);
    if (attachToContractId) {
      // Anlage AUS einem Vertrag heraus („Risiko zum Vertrag hinzufügen"):
      // zurück zum Vertrag. Diese Navigation lag früher im KFZ-Zweig der
      // NewRiskPage — dort lief sie am Adapter vorbei, das Fenster blieb
      // offen und behielt sein Dirty-Flag.
      navigate(`/vertraege/edit/${attachToContractId}`);
      return;
    }
    if (editRiskId) {
      // Beim Edit: zurück auf die Risiko-Detail-Seite (die der User gerade verlässt).
      // Steht sie schon darunter (KFZ/Immobilie öffnen dort automatisch das
      // Formular), führt sie selbst zurück — ein zweiter Eintrag in der
      // History ließe „Zurück" auf derselben Seite landen.
      if (window.location.pathname !== `/risks/${editRiskId}`) navigate(`/risks/${editRiskId}`);
      return;
    }
    if (contactId) {
      navigate(`/contacts/${contactId}`, { state: { defaultTab: "risiken" } });
    } else {
      navigate("/risks");
    }
  }, [
    closeWindow,
    setWindowDirty,
    navigate,
    windowId,
    contactId,
    attachToContractId,
    editRiskId,
  ]);

  const handleDirtyChange = React.useCallback(
    (dirty: boolean) => setWindowDirty(windowId, dirty),
    [setWindowDirty, windowId],
  );

  // „Zurück" in der Vollseite/​im Fenster: mit Eingaben erst nachfragen
  // (Verwerfen / Minimieren / Abbrechen), sonst direkt schließen.
  const [confirmClose, setConfirmClose] = React.useState(false);
  const handleClose = React.useCallback(() => {
    if (selfRef.current?.dirty) {
      setConfirmClose(true);
      return;
    }
    closeWindow(windowId);
  }, [closeWindow, windowId]);

  /**
   * Typwechsel aus dem Picker-Dialog: dieselbe Instanz kann die Props nicht
   * ändern (die render-Closure friert sie ein), deshalb close + open mit NEUER
   * ID. `attachToContractId` muss explizit mit, sonst verliert der Flow aus
   * dem Vertrag heraus seinen Vertragsbezug.
   */
  const handleReplace = React.useCallback(
    (nextType: string, nextContactId: number | null, nextContactName: string | null) => {
      const current = selfRef.current;
      const wasPage = current?.state === "page";
      closeWindow(windowId);
      openRiskFormWindow(openWindow, {
        riskType: nextType,
        contactId: nextContactId,
        contactName: nextContactName,
        attachToContractId: attachToContractId ?? null,
        // Die Fenster-Maße der ersetzten Instanz übernehmen (im Vollseiten-
        // Modus stecken sie in `preMax`), damit „Als Fenster" nach einem
        // Typwechsel dieselbe Größe liefert wie davor.
        initialSize: current?.preMax?.size ?? current?.size,
        mode: wasPage ? "page" : "window",
      });
    },
    [closeWindow, openWindow, windowId, attachToContractId],
  );

  const titleBase = React.useMemo(
    () => buildRiskTitleBase(riskType, contactName, contactId, Boolean(editRiskId)),
    [riskType, contactName, contactId, editRiskId],
  );

  const handleTitleSuffix = React.useCallback(
    (suffix: string | null) => {
      const trimmed = suffix?.trim();
      // Ohne erfasste Anschrift/Kennzeichen bleibt es beim Basistitel.
      if (!trimmed) {
        setWindowTitle(windowId, titleBase);
        return;
      }
      if (riskType === "immobilie") {
        // Minimiert steht das Fenster als Chip im Dock — dort zählt zuerst der
        // Kunde, dann das Objekt (Auftraggeber, 19.08.2026). Bewusst die
        // umgekehrte Reihenfolge zur Kontextzeile im Formular, die mit der
        // Objektart beginnt.
        const name = contactName?.trim();
        setWindowTitle(windowId, name ? `${name} — ${trimmed}` : trimmed);
        return;
      }
      setWindowTitle(windowId, `${titleBase} · ${trimmed}`);
    },
    // `riskType`/`contactName` sind unveränderliche Instanz-Parameter — die
    // Identität des Callbacks bleibt damit über Fenster-Zustände hinweg stabil.
    [setWindowTitle, windowId, titleBase, riskType, contactName],
  );

  // Stabile Identität des embedded-Objekts — sonst bekämen die useCallbacks in
  // NewRiskPage (Abhängigkeit `[embedded]`) bei JEDER Window-State-Änderung
  // eine neue Referenz und die useEffects der Step-Formulare feuerten erneut.
  // Deshalb hängt hier NICHTS am Fenster-Zustand (siehe selfRef oben); die
  // Deps sind ausschließlich die unveränderlichen Instanz-Parameter.
  const embedded = React.useMemo(
    () => ({
      windowId,
      riskType,
      kontaktId: contactId ?? null,
      // Für die Kontextzeile des Immobilien-Formulars. Unveränderlicher
      // Instanz-Parameter, hängt also nicht am Fenster-Zustand — die von
      // riskFormWindowEmbedded.test.tsx gepinnte Referenz-Stabilität bleibt.
      kontaktName: contactName ?? null,
      attachToContractId: attachToContractId ?? null,
      editRiskId: editRiskId ?? null,
      onSuccess: handleSuccess,
      onTitleSuffix: handleTitleSuffix,
      onDirtyChange: handleDirtyChange,
      onClose: handleClose,
      onReplace: handleReplace,
    }),
    [
      windowId,
      riskType,
      contactId,
      contactName,
      attachToContractId,
      editRiskId,
      handleSuccess,
      handleTitleSuffix,
      handleDirtyChange,
      handleClose,
      handleReplace,
    ],
  );

  return (
    <PageChromeContext.Provider value={isPage}>
      <NewRiskPage embedded={embedded} />
      <WindowConfirmDialog
        open={confirmClose}
        title="Ungespeicherte Eingaben"
        message="Das Formular enthält ungespeicherte Eingaben. Verwerfen, oder für später minimieren?"
        onDiscard={() => {
          setConfirmClose(false);
          setWindowDirty(windowId, false);
          closeWindow(windowId);
        }}
        onMinimize={() => {
          setConfirmClose(false);
          minimizeWindow(windowId);
        }}
        onCancel={() => setConfirmClose(false)}
      />
    </PageChromeContext.Provider>
  );
};

export default RiskFormWindow;
